import { useState, useEffect, useCallback } from 'react';
import { gamificationApi } from '../api';

/**
 * 新機能バッジ管理用のカスタムフック
 * 
 * ランクアップで解放された機能に「NEW」バッジを表示し、
 * ユーザーがその機能ページにアクセスしたらバッジを消す
 */

// ローカルストレージのキー
const SEEN_FEATURES_KEY = 'magicss_seen_features';

/**
 * 解放済みだがまだ見ていない機能を取得
 */
export const useNewFeatureBadge = () => {
  const [unlockedFeatures, setUnlockedFeatures] = useState({});
  const [seenFeatures, setSeenFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  // ローカルストレージから見た機能を取得
  const loadSeenFeatures = useCallback(() => {
    try {
      const stored = localStorage.getItem(SEEN_FEATURES_KEY);
      if (stored) {
        setSeenFeatures(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load seen features:', error);
    }
  }, []);

  // 機能解放状態を取得
  const loadUnlockedFeatures = useCallback(async () => {
    try {
      // 認証トークンがセットされていない場合はスキップ
      const token = localStorage.getItem('magicss_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const features = await gamificationApi.getAllFeaturesStatus();
      setUnlockedFeatures(features || {});
    } catch (error) {
      // 401エラーは認証前の呼び出しなのでログ出力しない
      if (error?.response?.status !== 401) {
        console.error('Failed to load unlocked features:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSeenFeatures();
    loadUnlockedFeatures();
  }, [loadSeenFeatures, loadUnlockedFeatures]);

  /**
   * 機能を「見た」としてマーク
   */
  const markFeatureAsSeen = useCallback((featureKey) => {
    setSeenFeatures(prev => {
      if (prev.includes(featureKey)) return prev;
      const newSeen = [...prev, featureKey];
      try {
        localStorage.setItem(SEEN_FEATURES_KEY, JSON.stringify(newSeen));
      } catch (error) {
        console.error('Failed to save seen features:', error);
      }
      return newSeen;
    });
  }, []);

  /**
   * 機能がNEWバッジを表示すべきかどうか
   */
  const shouldShowBadge = useCallback((featureKey) => {
    const feature = unlockedFeatures[featureKey];
    if (!feature || !feature.unlocked) return false;
    return !seenFeatures.includes(featureKey);
  }, [unlockedFeatures, seenFeatures]);

  /**
   * 特定のパスに対応する機能キーを取得
   */
  const getFeatureKeyForPath = useCallback((path) => {
    const pathToFeature = {
      '/aio/style': 'style_fine_tuning',
      '/aio/original': 'topping_data',
    };
    return pathToFeature[path] || null;
  }, []);

  /**
   * パスに対してNEWバッジを表示すべきか
   */
  const shouldShowBadgeForPath = useCallback((path) => {
    const featureKey = getFeatureKeyForPath(path);
    if (!featureKey) return false;
    return shouldShowBadge(featureKey);
  }, [getFeatureKeyForPath, shouldShowBadge]);

  /**
   * パスにアクセスした時にバッジを消す
   */
  const markPathAsSeen = useCallback((path) => {
    const featureKey = getFeatureKeyForPath(path);
    if (featureKey) {
      markFeatureAsSeen(featureKey);
    }
  }, [getFeatureKeyForPath, markFeatureAsSeen]);

  return {
    loading,
    unlockedFeatures,
    shouldShowBadge,
    shouldShowBadgeForPath,
    markFeatureAsSeen,
    markPathAsSeen,
  };
};

export default useNewFeatureBadge;
